import { useMandateSummary } from "../hooks/useMandateSummary.ts";

// Matches the dashboard palette (hardcoded, like the box-whisker chart).
const TEXT = "#cfe0ff";
const MUTED = "#8a93a6";
const AMBER = "#e0a458";
const GREEN = "#6fcf97";
const BORDER = "rgba(255,255,255,0.08)";
const CARD_BG = "rgba(255,255,255,0.03)";

// 30d-relative tightness label -> chip styling.
const CHIP: Record<string, { text: string; color: string }> = {
  tight: { text: "tight", color: GREEN },
  typical: { text: "typical", color: MUTED },
  wide: { text: "wide", color: AMBER },
  insufficient: { text: "insufficient recent quotes", color: MUTED },
  unknown: { text: "n/a", color: MUTED },
};

function fmt(n: number | null | undefined, digits = 1): string {
  return n == null ? "—" : n.toFixed(digits);
}

export function QuoteQualityCard({
  venue,
  pair,
  clientId,
}: {
  venue: string;
  pair: string;
  clientId?: string;
}) {
  const q = useMandateSummary({ venue, pair, clientId });
  const qq = q.data?.quote_quality;
  const chip = CHIP[qq?.label ?? "unknown"] ?? CHIP.unknown;

  // This card is current-vs-baseline (unlike the 30d-headline capture/adverse
  // cards), so it carries NO "30d" header tag -- the headline is the CURRENT
  // quoted spread, shown beside its 30d median benchmark on the same line.
  // The "tight/typical/wide" chip is the verdict of current-vs-median. vs-median
  // is signed: + = wider than typical (amber), − = tighter (green). Mean stays
  // in the footer as the skew tell (mean != median => skewed distribution).
  // Below the recent-sample floor the headline number is still exact and shown,
  // but the tight/typical/wide verdict and the vs-typical-median judgment are
  // withheld -- a categorical claim off a few quotes is not trustworthy.
  const insufficient = qq?.label === "insufficient";
  const vm = qq?.vs_median_pct ?? null;
  const vmColor = vm == null ? MUTED : vm > 0 ? AMBER : GREEN;
  const vmText =
    vm == null ? "—" : `${vm > 0 ? "+" : ""}${vm.toFixed(0)}% vs typical median`;

  return (
    <div
      style={{
        background: CARD_BG,
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        padding: "12px 14px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 12, color: MUTED }}>Quote quality</span>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: MUTED }}>30d</span>
          <span
            style={{
              fontSize: 11,
              padding: "2px 8px",
              borderRadius: 5,
              color: chip.color,
              border: `1px solid ${chip.color}55`,
            }}
          >
            {chip.text}
          </span>
        </span>
      </div>

      {/* current quoted spread beside its 30d median benchmark, same line */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
        <span style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span
            style={{
              fontSize: 24,
              fontWeight: 500,
              color: TEXT,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {fmt(qq?.recent_quoted_bps)}
          </span>
          <span style={{ fontSize: 12, color: MUTED }}>bps current</span>
        </span>
        <span style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span
            style={{
              fontSize: 24,
              fontWeight: 500,
              color: TEXT,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {fmt(qq?.median_30d_quoted_bps)}
          </span>
          <span style={{ fontSize: 12, color: MUTED }}>30d median</span>
        </span>
      </div>

      <div style={{ fontSize: 12, color: MUTED, marginTop: 6, lineHeight: 1.5 }}>
        {insufficient ? (
          <span style={{ color: MUTED }}>
            {qq?.n_recent ?? 0} recent quote
            {(qq?.n_recent ?? 0) === 1 ? "" : "s"} (last 60m)
          </span>
        ) : (
          <span style={{ color: vmColor }}>{vmText}</span>
        )}
        <br />
        two-sided uptime{" "}
        <span style={{ color: TEXT, fontVariantNumeric: "tabular-nums" }}>
          {fmt(qq?.two_sided_uptime_pct, 0)}%
        </span>{" "}
        ({qq?.uptime_window_hours ?? 24}h)
      </div>

      <div
        style={{
          fontSize: 11,
          color: MUTED,
          marginTop: 8,
          borderTop: `1px solid ${BORDER}`,
          paddingTop: 7,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        30d mean {fmt(qq?.mean_30d_quoted_bps)}
      </div>

      {q.isError && (
        <div style={{ fontSize: 11, color: AMBER, marginTop: 6 }}>
          could not load
        </div>
      )}
    </div>
  );
}
